import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Template, TemplateForm, PaginationParams, PaginationResponse } from '@/types'
import apiClient from '@/lib/api/client'
import { getMyTemplates as sdkGetTemplates, getTemplateDetail as sdkGetTemplateDetail, createMyTemplate as sdkCreateTemplate, updateMyTemplate as sdkUpdateTemplate, deleteMyTemplate as sdkDeleteTemplate } from '@/lib/api/generated/sdk.gen'

// 获取模板列表
export const useTemplates = (params: PaginationParams & { 
  search?: string
  status?: string
  templateType?: string
}) => {
  return useQuery({
    queryKey: ['templates', params],
    queryFn: async (): Promise<PaginationResponse<Template>> => {
      // 构建查询参数
      const queryParams: any = { 
        page: params.page, 
        size: params.size
      }
      
      // 添加搜索参数
      if (params.search) {
        queryParams.templateName = params.search
      }
      
      // 添加状态筛选
      if (params.status) {
        queryParams.status = params.status
      }
      
      // 添加模板类型筛选
      if (params.templateType) {
        queryParams.templateType = params.templateType
      }
      
      const res: any = await sdkGetTemplates({ client: apiClient, responseStyle: 'fields', throwOnError: false, query: queryParams })
      const payload: any = (res as any)?.data ?? res
      return payload as PaginationResponse<Template>
    },
  })
}

// 获取模板详情
export const useTemplate = (id: string) => {
  return useQuery({
    queryKey: ['template', id],
    queryFn: async (): Promise<Template> => {
      const res: any = await sdkGetTemplateDetail({ client: apiClient, responseStyle: 'fields', throwOnError: false, path: { templateCode: id } as any })
      return ((res as any)?.data ?? res) as Template
    },
    enabled: !!id,
  })
}

// 创建模板
export const useCreateTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: TemplateForm): Promise<Template> => {
      // 转换前端数据格式为后端期望的格式
      const requestData = {
        templateName: data.templateName,
        templateContent: data.templateContent,
        templateType: data.templateType,
        signatureId: data.signatureId ? parseInt(data.signatureId.toString()) : undefined,
        applyReason: data.applyReason,
      }
      
      const res: any = await sdkCreateTemplate({ client: apiClient, responseStyle: 'fields', throwOnError: false, body: requestData as any })
      return ((res as any)?.data ?? res) as Template
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

// 更新模板
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TemplateForm> }): Promise<Template> => {
      // 转换前端数据格式为后端期望的格式
      const requestData: any = {}
      if (data.templateName !== undefined) requestData.templateName = data.templateName
      if (data.templateContent !== undefined) requestData.templateContent = data.templateContent
      if (data.templateType !== undefined) requestData.templateType = data.templateType
      if (data.signatureId !== undefined) requestData.signatureId = data.signatureId ? parseInt(data.signatureId.toString()) : undefined
      if (data.applyReason !== undefined) requestData.applyReason = data.applyReason
      
      const res: any = await sdkUpdateTemplate({ client: apiClient, responseStyle: 'fields', throwOnError: false, path: { id } as any, body: requestData as any })
      return ((res as any)?.data ?? res) as Template
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      queryClient.invalidateQueries({ queryKey: ['template', id] })
    },
  })
}

// 删除模板
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await sdkDeleteTemplate({ client: apiClient, responseStyle: 'fields', throwOnError: false, path: { id } as any })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}